import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildAdminQueryRoot, buildScopedAdminQueryKey } from '@/lib/organization-scoping'
import { getStripeMigration, requestStripeMigration } from './stripe-migration.api'

export const stripeMigrationPolling = {
  background: 5 * 60 * 1000,
  upgrade: 5000,
}

export const stripeMigrationQueries = {
  status: ({ organizationId, polling = 'background' }) =>
    queryOptions({
      queryKey: buildScopedAdminQueryKey(
        buildAdminQueryRoot({ entity: 'stripeMigration' }, { organizationId }),
        { organizationId, scope: 'status' }
      ),
      queryFn: ({ signal }) => getStripeMigration({ organizationId, signal }),
      // Polling reads progress; the worker advances it. Only the upgrade screen needs fast updates.
      staleTime: polling === 'upgrade' ? 0 : stripeMigrationPolling.background,
      refetchIntervalInBackground: false,
      refetchInterval: ({ state: { data } }) =>
        data?.started && (!data.activated || (data.recovery && data.recovery.status !== 'active'))
          ? stripeMigrationPolling[polling]
          : false,
    }),
}

export function useStripeMigration(organizationId, { polling = 'background', ...options } = {}) {
  return useQuery({ ...stripeMigrationQueries.status({ organizationId, polling }), ...options })
}

export function useStripeMigrationAction(organizationId, action) {
  const queryClient = useQueryClient()
  const queryKey = stripeMigrationQueries.status({ organizationId }).queryKey
  return useMutation({
    mutationFn: () => requestStripeMigration({ organizationId, action }),
    onSuccess: async (status) => {
      // A poll started before this response must not restore the previous workflow step.
      await queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.setQueryData(queryKey, status)
    },
  })
}
